// /clients/volno — Přehled VOLNA klientů (dovolená / nemoc / jiné) na jednom místě.
// Zdroj: PG tabulka klient_rozvrh_absence (endpoint /api/klient-rozvrh-absence?all=1).
// Volno klienta se promítá do detekce pravidelného termínu v rozvrh vzoru (trener-rozvrh-pattern).
//
// Features:
//   - KPI klikací dlaždice (Probíhá dnes / Nadcházející / Celkem) s filtrem
//   - Search podle jména klienta + poznámky
//   - Filtr důvodu
//   - Přidat volno (autocomplete klienta + rozsah dat + důvod + poznámka)
//   - Smazat záznam (confirm dialog)
//
// 9.9.2026 (fáze 4b): sjednoceno do vzorníku. Akce v hlavičce jsou ikony, KPI
// jedou přes `DlazdiceFiltr`, obě okna přes `Modal` (na telefonu vyjedou
// zespodu a patka se odlepí od home indikátoru), pole přes `.field`/`.label`,
// důvod přes `Stitek` s tóny, koš má 44 px. Endpointy, práva rolí, potvrzování
// ani hromadné vkládání se nemění.

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plane, Plus, RefreshCw, AlertTriangle, ArrowLeft, Search, Trash2,
  Umbrella, Thermometer, CalendarDays, CalendarClock, CalendarCheck,
} from "lucide-react";
import Card from "../components/Card.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import InfoTip from "../components/InfoTip.jsx";
import ClientAutocomplete, { CLIENT_AUTOCOMPLETE_TOOLTIP, normalizeLoose } from "../components/ClientAutocomplete.jsx";
import { Stranka, Modal, Field, DlazdiceFiltr, Stitek, EmptyState, SkeletonRows, Hlaska, AkceZnovu } from "../components/ui.jsx";
import { useClientsList } from "../lib/clientsCache.js";
import { useApiData, apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { toast } from "../components/Toast.jsx";
import FiremniVolnoDialog, { FiremniVolnoZadosti } from "../components/FiremniVolnoDialog.jsx";

const REASONS = [
  { value: "dovolena", label: "Dovolená", icon: Umbrella },
  { value: "nemoc", label: "Nemoc", icon: Thermometer },
  { value: "jine", label: "Jiné", icon: CalendarDays },
];
const REASON_LABEL = { dovolena: "Dovolená", nemoc: "Nemoc", jine: "Jiné volno" };
// Tóny ze vzorníku, ne vlastní tabulka barev.
const REASON_TON = { dovolena: "info", nemoc: "problem", jine: "neutralni" };

function fmtCz(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}` : iso;
}
function daysBetween(from, to) {
  try {
    const a = new Date(from), b = new Date(to || from);
    if (isNaN(a) || isNaN(b)) return 0;
    return Math.round((b - a) / 86400000) + 1;
  } catch { return 0; }
}
function dayWord(n) { return n === 1 ? "den" : n >= 2 && n <= 4 ? "dny" : "dní"; }
function prettyUser(v) {
  const s = String(v || "").trim();
  if (!s || s === "admin") return s || "—";
  return s.includes("@") ? s.split("@")[0] : s;
}
const todayIso = () => new Date().toISOString().substring(0, 10);

/** Výběr jedné možnosti dlaždicemi (důvod, komu). */
function VyberDlazdice({ items, value, onChange, sloupce = 3 }) {
  return (
    <div className={`grid gap-2 ${sloupce === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
      {items.map((r) => {
        const Icon = r.icon;
        const active = value === r.value;
        return (
          <button key={r.value} type="button" onClick={() => onChange(r.value)} aria-pressed={active}
            className={`flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-sm font-semibold transition ${
              active ? "border-2 border-brand bg-brand-50 text-brand-deep" : "border-slate-300 bg-white text-slate-600 hover:border-brand/40"
            }`}>
            {Icon && <Icon className="h-4 w-4" />} {r.label}
          </button>
        );
      })}
    </div>
  );
}

function PotvrzeniOkno({ title, message, confirmLabel, cancelLabel, onConfirm, onCancel }) {
  return (
    <Modal
      open
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <button type="button" onClick={onCancel} className="btn btn-secondary">{cancelLabel || "Zrušit"}</button>
          <button type="button" onClick={onConfirm} className="btn btn-danger">{confirmLabel || "Smazat"}</button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 text-sm text-slate-600">{message}</div>
      </div>
    </Modal>
  );
}

function AddModal({ klientOptions, onClose, onSubmit, submitting }) {
  const today = todayIso();
  const [klient, setKlient] = useState("");
  const [form, setForm] = useState({ dateFrom: "", dateTo: "", duvod: "dovolena", poznamka: "" });
  const [err, setErr] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const days = daysBetween(form.dateFrom, form.dateTo || form.dateFrom);
  const canSubmit = klient.trim() && form.dateFrom;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Přidat volno klienta"
        size="sm"
        footer={
          <>
            <button type="button" onClick={onClose} className="btn btn-secondary">Zrušit</button>
            <button type="button" disabled={submitting || !canSubmit}
              onClick={() => { if (!canSubmit) { setErr("Vyplň klienta a datum od"); return; } setErr(null); setConfirm(true); }}
              className="btn btn-primary">
              {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {submitting ? "Ukládám…" : "Přidat volno"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <div className="label flex items-center gap-1.5">Klient <InfoTip text={CLIENT_AUTOCOMPLETE_TOOLTIP} /></div>
            <ClientAutocomplete value={klient} onChange={setKlient} options={klientOptions} accentColor="violet" placeholder="Začni psát jméno nebo příjmení…" autoFocus />
          </div>

          <div>
            <div className="label">Důvod</div>
            <VyberDlazdice items={REASONS} value={form.duvod} onChange={(v) => setForm((f) => ({ ...f, duvod: v }))} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Od">
              <input type="date" value={form.dateFrom} min={today}
                onChange={(e) => setForm((f) => ({ ...f, dateFrom: e.target.value }))} className="field" />
            </Field>
            <Field label="Do">
              <input type="date" value={form.dateTo} min={form.dateFrom || today}
                onChange={(e) => setForm((f) => ({ ...f, dateTo: e.target.value }))} className="field" />
            </Field>
          </div>

          <Field label="Poznámka" hint="Nepovinné, např. dovolená u moře.">
            <input type="text" value={form.poznamka} placeholder="Např. dovolená u moře"
              onChange={(e) => setForm((f) => ({ ...f, poznamka: e.target.value }))} className="field" />
          </Field>

          {err && <Hlaska ton="problem" title="Chybí údaj">{err}</Hlaska>}

          {form.dateFrom && (
            <div className="rounded-xl bg-brand-50 px-3 py-2 text-[12.5px] font-semibold text-brand-deep">
              {fmtCz(form.dateFrom)}{form.dateTo && form.dateTo !== form.dateFrom ? ` – ${fmtCz(form.dateTo)}` : ""}, {days} {dayWord(days)}
            </div>
          )}
        </div>
      </Modal>

      {confirm && (
        <PotvrzeniOkno
          title="Přidat volno klienta?"
          message={<div className="space-y-1"><div><strong className="text-slate-900">{klient}</strong></div><div className="text-xs text-slate-500">{REASON_LABEL[form.duvod]}, {fmtCz(form.dateFrom)}{form.dateTo && form.dateTo !== form.dateFrom ? ` – ${fmtCz(form.dateTo)}` : ""}</div></div>}
          confirmLabel="Ano, přidat" cancelLabel="Zpět"
          onConfirm={() => { setConfirm(false); onSubmit({ klient: klient.trim(), dateFrom: form.dateFrom, dateTo: form.dateTo || form.dateFrom, duvod: form.duvod, poznamka: form.poznamka }); }}
          onCancel={() => setConfirm(false)}
        />
      )}
    </>
  );
}

export default function KlientVolno() {
  const navigate = useNavigate();
  const { data, loading, error, refetch, refreshing } = useApiData("/api/klient-rozvrh-absence?all=1");
  const currentUser = getCurrentUser();
  const canDelete = currentUser && ["owner", "manager", "csm"].includes(currentUser.role);
  // 3. 11. 2026: hromadné volno nahradilo Firemní volno. Manager a CSM navrhují, owner schvaluje.
  const canBulk = currentUser && ["owner", "manager", "csm"].includes(currentUser.role);
  const jeOwner = currentUser && currentUser.role === "owner";
  const { data: fvData, refetch: fvRefetch } = useApiData(canBulk ? "/api/firemni-volno?prehled=1" : null, { bezKese: true });
  const [fvBusy, setFvBusy] = useState(null);
  const klientOptions = useClientsList();

  const [filter, setFilter] = useState("all"); // all | dnes | nadchazejici
  const [filterDuvod, setFilterDuvod] = useState("all");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showBulk, setShowBulk] = useState(false);

  const items = data?.items || [];
  const today = todayIso();

  const stats = useMemo(() => {
    let dnes = 0, nadch = 0;
    for (const x of items) {
      const f = x.date_from, t = x.date_to || x.date_from;
      if (f <= today && t >= today) dnes++;
      else if (f > today) nadch++;
    }
    return { dnes, nadch, total: items.length };
  }, [items, today]);

  const filtered = useMemo(() => {
    return items.filter((x) => {
      const f = x.date_from, t = x.date_to || x.date_from;
      if (filter === "dnes" && !(f <= today && t >= today)) return false;
      if (filter === "nadchazejici" && !(f > today)) return false;
      if (filterDuvod !== "all" && x.duvod !== filterDuvod) return false;
      if (search) {
        const tokens = normalizeLoose(search).split(" ").filter(Boolean);
        const hay = normalizeLoose(`${x.klient_name} ${x.poznamka || ""} ${x.zadavatel || ""}`);
        if (!tokens.every((tk) => hay.includes(tk))) return false;
      }
      return true;
    });
  }, [items, filter, filterDuvod, search, today]);

  const handleAdd = async (payload) => {
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/klient-rozvrh-absence", {
        method: "POST",
        body: JSON.stringify({ klient: payload.klient, dateFrom: payload.dateFrom, dateTo: payload.dateTo, duvod: payload.duvod, poznamka: payload.poznamka }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setShowAdd(false);
      refetch();
    } catch (e) {
      toast(`Chyba uložení: ${e.message}`, "error");
    } finally { setSubmitting(false); }
  };

  // Provedení může trvat déle (SMS, úkoly). Když server nestihne všechno,
  // vrátí hotovo: false a tady se pokračuje akcí dokoncit.
  const dokonci = async (id, j) => {
    let posledni = j;
    for (let i = 0; i < 8 && posledni && posledni.hotovo === false; i++) {
      const r = await apiFetch("/api/firemni-volno", { method: "POST", body: JSON.stringify({ akce: "dokoncit", id }) });
      posledni = await r.json().catch(() => ({}));
      if (!r.ok || !posledni.ok) throw new Error(posledni.error || "Dokončení se nepovedlo, zkuste to znovu.");
    }
    return posledni;
  };
  const souhrnText = (s) => {
    const x = s || {};
    const casti = [];
    if (x.ukoly) casti.push(`úkoly: ${x.ukoly.treneru || 0} trenérům${x.ukoly.nutricni ? ", nutriční" : ""}${x.ukoly.csm ? ", CSM" : ""}`);
    if (x.lekce) casti.push(`bootcamp: zrušeno ${x.lekce.zruseno || 0}, smazáno ${x.lekce.smazano || 0} lekcí, SMS ${x.smsOdeslano || 0}`);
    if (x.platnost) casti.push(`prodlouženo ${(x.platnost.bootcamp || 0) + (x.platnost.osobni || 0) + (x.platnost.deti || 0)} permanentek`);
    return casti.join(", ");
  };
  const poProvedeni = async (j) => {
    const hotovo = await dokonci(j.id, j);
    toast(`Firemní volno je schválené. ${souhrnText(hotovo && hotovo.souhrn)}`, "success");
    fvRefetch(); refetch();
  };
  const schvalitVolno = async (z) => {
    setFvBusy(z.id);
    try {
      const r = await apiFetch("/api/firemni-volno", { method: "POST", body: JSON.stringify({ akce: "schvalit", id: z.id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
      await poProvedeni(j);
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); fvRefetch(); } finally { setFvBusy(null); }
  };
  const zamitnoutVolno = async (z) => {
    setFvBusy(z.id);
    try {
      const r = await apiFetch("/api/firemni-volno", { method: "POST", body: JSON.stringify({ akce: "zamitnout", id: z.id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
      toast("Návrh firemního volna je zamítnutý.", "success");
      fvRefetch();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); } finally { setFvBusy(null); }
  };
  const nedokoncena = (fvData?.schvalena || []).filter((v) => !v.hotovo);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      const res = await apiFetch("/api/klient-rozvrh-absence", { method: "DELETE", body: JSON.stringify({ id: target.id }) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      refetch();
    } catch (e) {
      toast(`Chyba mazání: ${e.message}`, "error");
      refetch();
    }
  };

  const akce = [
    { klic: "zpet", ikona: ArrowLeft, popis: "Zpět na klienty", onClick: () => navigate("/clients") },
    { klic: "pridat", ikona: Plus, popis: "Přidat volno", onClick: () => setShowAdd(true) },
    ...(canBulk ? [{ klic: "hromadne", ikona: Plane, popis: "Firemní volno", onClick: () => setShowBulk(true) }] : []),
    { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading || refreshing },
  ];

  return (
    <Stranka>
      <GradientHeader
        title="Dovolené klientů"
        subtitle="Promítá se do detekce pravidelného termínu v rozvrh vzoru."
        icon={Plane}
        loading={loading}
        akce={akce}
      />

      {error && <Hlaska ton="problem" title="Záznamy se nenačetly" akce={<AkceZnovu onClick={refetch} />}>{String(error)}</Hlaska>}

      {jeOwner && <FiremniVolnoZadosti zadosti={fvData?.ceka || []} onSchvalit={schvalitVolno} onZamitnout={zamitnoutVolno} busyId={fvBusy} />}
      {jeOwner && nedokoncena.map((v) => (
        <Hlaska key={v.id} ton="pozor" title={`Firemní volno „${v.nazev}“ se neprovedlo celé`}
          akce={<button type="button" className="btn-primary" disabled={fvBusy === v.id}
            onClick={async () => { setFvBusy(v.id); try { await poProvedeni({ id: v.id, hotovo: false }); } catch (e) { toast(`Chyba: ${e.message}`, "error"); } finally { setFvBusy(null); } }}>Dokončit</button>}>
          Některé kroky (úkoly, bootcamp, SMS, platnost) ještě neproběhly, klikněte na Dokončit.
        </Hlaska>
      ))}
      {!jeOwner && (fvData?.ceka || []).length > 0 && (
        <Hlaska ton="pozor" title="Firemní volno čeká na schválení ownerem">
          {(fvData.ceka || []).map((z) => `${z.nazev} (${fmtCz(z.od)} až ${fmtCz(z.do)})`).join(", ")}
        </Hlaska>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <DlazdiceFiltr popisek="Probíhá dnes" hodnota={stats.dnes} ikona={CalendarCheck} ton="pozor"
          info={<InfoTip text="Klienti, kteří mají dnes volno (dnešek spadá do zadaného rozsahu). Klik filtruje." />}
          zapnuto={filter === "dnes"} onClick={() => setFilter(filter === "dnes" ? "all" : "dnes")} />
        <DlazdiceFiltr popisek="Nadcházející" hodnota={stats.nadch} ikona={CalendarClock} ton="info"
          info={<InfoTip text="Volno naplánované do budoucna (začátek je po dnešku). Klik filtruje." />}
          zapnuto={filter === "nadchazejici"} onClick={() => setFilter(filter === "nadchazejici" ? "all" : "nadchazejici")} />
        <DlazdiceFiltr popisek="Celkem záznamů" hodnota={stats.total} ikona={CalendarDays} ton="neutralni"
          info={<InfoTip text="Všechny záznamy volna klientů v databázi (i historické)." />}
          className="col-span-2 sm:col-span-1" />
      </div>

      <Card title="Záznamy" ikona={CalendarDays} lista odznak={data ? `${filtered.length} z ${items.length}` : undefined}>
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(220px,1fr)_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hledat klienta, i jen příjmení…"
              aria-label="Hledat klienta" className="field pl-9" />
          </div>
          <select value={filterDuvod} onChange={(e) => setFilterDuvod(e.target.value)} aria-label="Filtrovat dle důvodu"
            className={`field ${filterDuvod !== "all" ? "border-brand font-semibold text-brand-deep" : ""}`}>
            <option value="all">Všechny důvody</option>
            <option value="dovolena">Dovolená</option>
            <option value="nemoc">Nemoc</option>
            <option value="jine">Jiné volno</option>
          </select>
        </div>

        {loading && !data ? (
          <SkeletonRows rows={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon={Plane} title={items.length === 0 ? "Žádné zadané volno klientů" : "Nic neodpovídá filtru"}
            hint={items.length === 0 ? "Přidej volno tlačítkem v hlavičce." : "Zkus zrušit filtr nebo hledání."} />
        ) : (
          <div className="space-y-2">
            {filtered.map((x) => {
              const f = x.date_from, t = x.date_to || x.date_from;
              const isPast = t < today;
              const isNow = f <= today && t >= today;
              const dd = daysBetween(x.date_from, x.date_to);
              return (
                <div key={x.id} className={`polozka ${
                  isPast ? "bg-slate-50 opacity-70" : isNow ? "border-amber-200 bg-amber-50/60" : ""
                }`}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[15px] font-bold leading-snug text-slate-900 demo-sensitive">{x.klient_name}</div>
                      <div className="mt-0.5 text-[12.5px] tabular-nums text-slate-500">
                        {fmtCz(x.date_from)}{x.date_to && x.date_to !== x.date_from ? ` – ${fmtCz(x.date_to)}` : ""}, {dd} {dayWord(dd)}
                      </div>
                    </div>
                    {canDelete && (
                      <button type="button" onClick={() => setDeleteTarget(x)} title="Smazat volno" aria-label="Smazat volno"
                        className="btn-ikona shrink-0 border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Stitek ton={REASON_TON[x.duvod] || "neutralni"} velikost="mala">{REASON_LABEL[x.duvod] || x.duvod || "volno"}</Stitek>
                    {isNow && <Stitek ton="pozor" velikost="mala">Právě teď</Stitek>}
                    {isPast && <Stitek ton="neutralni" velikost="mala">Proběhlo</Stitek>}
                    <span className="text-[11.5px] text-slate-400">zadal {prettyUser(x.zadavatel)}</span>
                  </div>
                  {x.poznamka && <div className="mt-1.5 text-[12.5px] leading-snug text-slate-600">{x.poznamka}</div>}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {showAdd && <AddModal klientOptions={klientOptions} onClose={() => setShowAdd(false)} onSubmit={handleAdd} submitting={submitting} />}
      <FiremniVolnoDialog open={showBulk} onClose={() => setShowBulk(false)}
        onHotovo={async (j) => {
          if (j && j.stav === "schvaleno") { try { await poProvedeni(j); } catch (e) { toast(`Chyba: ${e.message}`, "error"); fvRefetch(); } }
          else { toast("Firemní volno je odeslané ownerovi ke schválení.", "success"); fvRefetch(); }
        }} />
      {deleteTarget && (
        <PotvrzeniOkno
          title="Smazat volno klienta?"
          message={<div className="space-y-1"><div><strong className="text-slate-900">{deleteTarget.klient_name}</strong></div><div className="text-xs text-slate-500">{REASON_LABEL[deleteTarget.duvod]}, {fmtCz(deleteTarget.date_from)}{deleteTarget.date_to && deleteTarget.date_to !== deleteTarget.date_from ? ` – ${fmtCz(deleteTarget.date_to)}` : ""}</div></div>}
          confirmLabel="Ano, smazat" cancelLabel="Zpět"
          onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)}
        />
      )}
    </Stranka>
  );
}
